import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { sheetToForm, sheetTotals } from './commissionSheetForm';
import { optionistReportExceedsLimit } from './commissionOptionistReport';
import { useRealEstateCommissionInstallmentData } from './realEstateCommissionInstallmentData';
import { useRealEstateCommissionInstallmentWorkflow } from './realEstateCommissionInstallmentWorkflow';
import { useRealEstateCommissionInstallmentRows } from './useRealEstateCommissionInstallmentRows';

export function useRealEstateCommissionInstallment({
  id,
  t,
  notify,
  autoStartRevision = false,
}) {
  const installment = ref(null);
  const catalog = ref({});
  const form = ref(sheetToForm({}));
  const loading = ref(false);
  const loadingCatalog = ref(false);
  const saving = ref(false);
  const sending = ref(false);
  const revising = ref(false);
  const downloading = ref(false);
  const error = ref('');
  let saveTimer;

  const amountCents = computed(() => installment.value?.amount_cents || 0);
  const totals = computed(() => sheetTotals(form.value, amountCents.value));
  const permissions = computed(() => installment.value?.permissions || {});
  const optionistReportTypes = computed(
    () => catalog.value.tipo_laudo_opcionista || []
  );
  const optionistReport = computed(
    () => form.value.corretores_opcionistas?.[0]?.tipo_laudo_opcionista || ''
  );
  const optionistReportExceeds = computed(() =>
    optionistReportExceedsLimit(
      form.value.corretores_opcionistas || [],
      optionistReportTypes.value
    )
  );
  const canEdit = computed(() =>
    Boolean(
      permissions.value.can_edit &&
        (!installment.value?.enviada || installment.value?.revision_active)
    )
  );
  const canRevise = computed(
    () =>
      Boolean(permissions.value.can_revise) &&
      Boolean(installment.value?.enviada) &&
      !installment.value?.revision_locked
  );
  const canSend = computed(
    () =>
      Boolean(permissions.value.can_send) &&
      !installment.value?.enviada &&
      totals.value.complete &&
      !optionistReportExceeds.value
  );
  const canDownload = computed(() =>
    Boolean(
      permissions.value.can_download && installment.value?.download_available
    )
  );

  const { apply, load, loadCatalog, notifyError, save } =
    useRealEstateCommissionInstallmentData({
      canEdit,
      catalog,
      error,
      form,
      id,
      installment,
      loading,
      loadingCatalog,
      notify,
      saving,
      t,
    });

  const scheduleSave = () => {
    window.clearTimeout(saveTimer);
    saveTimer = window.setTimeout(() => save({ silent: true }), 5000);
  };
  const rowState = useRealEstateCommissionInstallmentRows({
    form,
    optionistReport,
    optionistReportTypes,
    scheduleSave,
  });

  const { download, send, startRevision } =
    useRealEstateCommissionInstallmentWorkflow({
      apply,
      canDownload,
      canEdit,
      canRevise,
      canSend,
      downloading,
      error,
      id,
      installment,
      loadCatalog,
      notify,
      notifyError,
      optionistReportExceeds,
      revising,
      save,
      sending,
      t,
    });

  onMounted(async () => {
    const loaded = await load();
    const shouldStartRevision =
      typeof autoStartRevision === 'object'
        ? autoStartRevision.value
        : autoStartRevision;
    if (loaded && shouldStartRevision && canRevise.value) {
      const revised = await startRevision({ silent: true });
      if (revised) return;
    }
    if (loaded && canEdit.value) await loadCatalog();
  });

  onBeforeUnmount(() => window.clearTimeout(saveTimer));

  return {
    ...rowState,
    amountCents,
    canDownload,
    canEdit,
    canRevise,
    canSend,
    catalog,
    download,
    downloading,
    error,
    form,
    installment,
    load,
    loading,
    loadingCatalog,
    permissions,
    optionistReport,
    optionistReportExceeds,
    optionistReportTypes,
    save,
    saving,
    scheduleSave,
    send,
    sending,
    revising,
    startRevision,
    totals,
  };
}
